import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import {
  Modal,
  ModalFooter,
  ModalButton,
  Input,
  Textarea,
} from './ui';

export default function POReceiptEditModal({
  isOpen,
  onClose,
  receipt,
  line,
  onSaved,
  onDeleted,
}) {
  const [form, setForm] = useState({
    received_quantity: '',
    received_date: '',
    packing_slip_ref: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen || !receipt) return;
    setForm({
      received_quantity: receipt.received_quantity ?? '',
      received_date: receipt.received_date ?? '',
      packing_slip_ref: receipt.packing_slip_ref ?? '',
      notes: receipt.notes ?? '',
    });
    setDeleteConfirmOpen(false);
  }, [isOpen, receipt]);

  const setField = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e?.target?.value ?? e }));
  };

  const handleSave = async () => {
    if (saving) return;
    const qty = Number(form.received_quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Received quantity must be greater than zero.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('purchase_order_receipts')
        .update({
          received_quantity: qty,
          received_date: form.received_date || new Date().toISOString().slice(0, 10),
          packing_slip_ref: form.packing_slip_ref?.trim() || null,
          notes: form.notes?.trim() || null,
        })
        .eq('id', receipt.id);
      if (error) throw error;
      toast.success('Receipt updated.');
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Receipt update failed:', err);
      toast.error(err.message || 'Failed to update receipt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('purchase_order_receipts')
        .delete()
        .eq('id', receipt.id);
      if (error) throw error;
      toast.success('Receipt deleted.');
      setDeleteConfirmOpen(false);
      onDeleted?.();
      onClose();
    } catch (err) {
      console.error('Receipt delete failed:', err);
      toast.error(err.message || 'Failed to delete receipt.');
    } finally {
      setDeleting(false);
    }
  };

  if (!receipt) return null;

  return (
    <>
      <Modal
        isOpen={isOpen && !deleteConfirmOpen}
        onClose={onClose}
        title={`Edit receipt · ${receipt.lot_number}`}
        size="md"
      >
        <div className="space-y-3">
          <div className="px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-sm">
            <span className="text-slate-500">Line {line?.line_number || '?'}</span>
            {line?.description ? <span className="text-slate-700"> · {line.description}</span> : null}
            <div className="mt-1 text-slate-500">
              Lot # <span className="font-mono text-slate-700">{receipt.lot_number}</span> · cannot be changed
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={`Received Quantity${line?.uom ? ` (${line.uom})` : ''}`}
              type="number"
              step="0.001"
              min="0"
              value={form.received_quantity}
              onChange={setField('received_quantity')}
              required
            />
            <Input
              label="Received Date"
              type="date"
              value={form.received_date}
              onChange={setField('received_date')}
              required
            />
          </div>

          <Input
            label="Packing Slip Ref"
            value={form.packing_slip_ref}
            onChange={setField('packing_slip_ref')}
            placeholder="optional"
          />

          <Textarea
            label="Notes"
            value={form.notes}
            onChange={setField('notes')}
            rows={2}
            placeholder="optional"
          />

          <ModalFooter>
            <ModalButton variant="danger" onClick={() => setDeleteConfirmOpen(true)} disabled={saving}>
              Delete
            </ModalButton>
            <div className="flex-1" />
            <ModalButton variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </ModalButton>
            <ModalButton variant="primary" onClick={handleSave} loading={saving}>
              Save
            </ModalButton>
          </ModalFooter>
        </div>
      </Modal>

      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete receipt?"
        size="md"
      >
        <p className="text-sm text-slate-700">
          This will delete lot <span className="font-mono text-slate-900">{receipt.lot_number}</span>{' '}
          ({Number(receipt.received_quantity)} {line?.uom || 'units'}). The line's received quantity
          will be recalculated automatically.
        </p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </ModalButton>
          <ModalButton variant="danger" onClick={handleDelete} loading={deleting}>
            Delete receipt
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
}
